import {
  FieldGrid,
  projectFieldState,
  type FieldPresentation,
  type FieldProjectionInput,
  type FieldTiming,
} from "@countdown/theme";
import { type ReactElement, useEffect } from "react";
import { Link, useLocation } from "react-router";

import { PageHeading } from "@/components/PageHeading";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { fixedClock } from "@/lib/clock";
import {
  HELP_SECTIONS,
  HELP_UPDATED_AT,
  type HelpAction,
  type HelpEntry,
} from "@/lib/help";
import {
  BOOKING_WORKFLOW,
  BOOKING_WORKFLOW_BY_STATE,
  type BookingWorkflowStep,
  PERMISSION_LABELS,
  type Permission,
} from "@/lib/labels";

function nextLabels(step: BookingWorkflowStep): string {
  if (step.next.length === 0) {
    return "keine";
  }
  return step.next
    .map((state) => BOOKING_WORKFLOW_BY_STATE[state].label)
    .join(", ");
}

/** The booking state machine, as the table the "Belegungen" screen used to carry. */
function BookingStates(): ReactElement {
  return (
    <Table>
      <TableCaption className="sr-only">Zustände einer Belegung</TableCaption>
      <TableHeader>
        <TableRow>
          <TableHead>Zustand</TableHead>
          <TableHead>Auslöser</TableHead>
          <TableHead>Wirkung</TableHead>
          <TableHead>Folgezustände</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {BOOKING_WORKFLOW.map((step) => (
          <TableRow key={step.state}>
            <TableCell className="font-medium">
              <span className="flex flex-col items-start gap-1">
                {step.label}
                {step.kind === "special" ? (
                  <Badge variant="outline">Sonderpfad</Badge>
                ) : null}
              </span>
            </TableCell>
            <TableCell>{step.trigger}</TableCell>
            <TableCell>{step.effect}</TableCell>
            <TableCell>{nextLabels(step)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/*
 * The door states at one fixed instant.
 *
 * Calls the SHARED projection and renders the SHARED grid with the shared stylesheet
 * (`@countdown/theme/fields.css`, imported in `index.css`), so the figure is the calendar's own
 * drawing and cannot drift from it (ADR 0001: preview, not replica). No `size`: single cells,
 * because the figure shows states, not the mosaic.
 */

/** Fixed instant, so the figure is reproducible and reviewable. */
const DEMO_NOW = fixedClock(new Date("2026-12-07T12:00:00.000Z")).now();
const DEMO_TIME_ZONE = "Europe/Berlin";

const DAY_MS = 24 * 60 * 60 * 1000;
const MINUTE_MS = 60 * 1000;

/** 22:00Z, i.e. 23:00 in Berlin on the demo day, so "heute bis 23:00 Uhr" holds. */
const ENDS_TONIGHT = 10 / 24;

function offset(days: number): string {
  return new Date(DEMO_NOW.getTime() + days * DAY_MS).toISOString();
}

function timing(
  unlocks: number,
  participationEnds: number,
  followUpEnds: number,
): FieldTiming {
  return {
    unlocksAt: offset(unlocks),
    participationEndsAt: offset(participationEnds),
    followUpEndsAt: offset(followUpEnds),
  };
}

interface DemoCase {
  readonly position: number;
  readonly caption: string;
  readonly input: Omit<FieldProjectionInput, "now" | "timeZone">;
}

const DEMO_CASES: readonly DemoCase[] = [
  {
    position: 1,
    caption: "Freischaltzeitpunkt liegt in der Zukunft",
    input: { timing: timing(1, 2, 30), isDepublished: false },
  },
  {
    position: 2,
    caption: "Verlosung läuft",
    input: { timing: timing(-0.5, ENDS_TONIGHT, 30), isDepublished: false },
  },
  {
    position: 3,
    caption: "Tagesfrist vorbei, Teilnahme verbucht und im Tagespool",
    input: {
      timing: timing(-2, -1, 30),
      isDepublished: false,
      viewer: { hasParticipation: true, includedInDraw: true },
    },
  },
  {
    position: 4,
    caption: "Tagesfrist vorbei, keine Teilnahme, Nachlauffrist läuft",
    input: { timing: timing(-2, -1, 30), isDepublished: false },
  },
  {
    position: 5,
    caption: "Nachlauffrist abgelaufen",
    input: { timing: timing(-40, -39, -1), isDepublished: false },
  },
  {
    position: 6,
    caption: "Not-Aus für dieses Türchen, überschreibt jeden anderen Zustand",
    input: {
      timing: timing(-0.5, ENDS_TONIGHT, 30),
      isDepublished: true,
      viewer: { hasParticipation: true, includedInDraw: true },
    },
  },
  {
    position: 7,
    caption: "Kulanzfenster: eigener Entwurf offen, kein eigener Zustand",
    input: {
      timing: timing(-2, -1, 30),
      isDepublished: false,
      viewer: {
        hasParticipation: false,
        draft: {
          graceWindowEndsAt: new Date(
            DEMO_NOW.getTime() + 40 * MINUTE_MS,
          ).toISOString(),
        },
      },
    },
  },
];

interface DemoRow {
  readonly position: number;
  readonly caption: string;
  readonly presentation: FieldPresentation;
}

const DEMO_ROWS: readonly DemoRow[] = DEMO_CASES.map((entry) => ({
  position: entry.position,
  caption: entry.caption,
  presentation: projectFieldState({
    ...entry.input,
    now: DEMO_NOW,
    timeZone: DEMO_TIME_ZONE,
  }),
}));

const INTERACTION_LABELS: Readonly<
  Record<FieldPresentation["interaction"], string>
> = {
  none: "keine",
  content_only: "nur Inhalt",
  participate: "Teilnahme",
  lead_only: "nur Registrierung",
};

function yesNo(value: boolean): string {
  return value ? "ja" : "nein";
}

function FieldStatesDemo(): ReactElement {
  return (
    <>
      <FieldGrid
        fields={DEMO_ROWS.map((row) => ({
          position: row.position,
          presentation: row.presentation,
        }))}
        label="Beispiel der Türchenzustände am 07.12.2026"
        fieldTerm="door"
        shape="square"
      />

      <Table className="mt-4">
        <TableCaption className="sr-only">
          Türchenzustände am 07.12.2026, 13:00 Uhr
        </TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead>Türchen</TableHead>
            <TableHead>Zustand</TableHead>
            <TableHead>Situation</TableHead>
            <TableHead>Möglich</TableHead>
            <TableHead>Inhalt</TableHead>
            <TableHead>Formular</TableHead>
            <TableHead>Hinweistext</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {DEMO_ROWS.map((row) => (
            <TableRow key={row.position}>
              <TableCell className="font-medium">{row.position}</TableCell>
              <TableCell>{row.presentation.label}</TableCell>
              <TableCell>{row.caption}</TableCell>
              <TableCell>
                {INTERACTION_LABELS[row.presentation.interaction]}
              </TableCell>
              <TableCell>{yesNo(row.presentation.showsContent)}</TableCell>
              <TableCell>{yesNo(row.presentation.showsAnswerForm)}</TableCell>
              <TableCell>{row.presentation.notice?.text ?? "kein"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
}

const FIGURES: Readonly<
  Record<NonNullable<HelpEntry["figure"]>, () => ReactElement>
> = {
  "booking-states": BookingStates,
  "field-states": FieldStatesDemo,
};

function WhereBox({
  actions,
}: {
  readonly actions: readonly HelpAction[];
}): ReactElement {
  return (
    <div className="border border-border bg-surface-sunken px-4 py-3">
      <p className="font-medium">Wo im Backoffice</p>
      <ul>
        {actions.map((action) => (
          <li key={`${action.where} ${action.label}`}>
            {action.path === undefined ? (
              action.where
            ) : (
              <Link className="underline underline-offset-4" to={action.path}>
                {action.where}
              </Link>
            )}
            , »{action.label}«
          </li>
        ))}
      </ul>
    </div>
  );
}

function Entry({ entry }: { readonly entry: HelpEntry }): ReactElement {
  const Figure = entry.figure === undefined ? null : FIGURES[entry.figure];

  return (
    <details
      id={entry.id}
      className="scroll-mt-4 border-b border-border last:border-b-0"
    >
      <summary className="cursor-pointer px-4 py-3 text-sm font-medium hover:bg-muted">
        {entry.question}
      </summary>
      <div className="flex flex-col gap-3 px-4 pb-4 text-sm">
        {entry.answer.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
        {Figure === null ? null : <Figure />}
        {entry.actions === undefined ? null : (
          <WhereBox actions={entry.actions} />
        )}
      </div>
    </details>
  );
}

/**
 * Help (`/hilfe`), the one place for explanations: the screens carry titles and labels only.
 *
 * Native `<details>` per question: keyboard and screen reader support come with the element,
 * and every question has an anchor (`/hilfe#stilllegen`) to point at.
 *
 * The anchor is followed here, not by the browser: the page renders only after the session
 * has loaded and a client-side link does not scroll at all, so a deep link would land at the
 * top with its answer folded shut.
 */
export function HelpPage({ roles }: { readonly roles: readonly string[] }): ReactElement {
  const { hash } = useLocation();

  useEffect(() => {
    const target = hash.length > 1 ? document.getElementById(hash.slice(1)) : null;
    if (target instanceof HTMLDetailsElement) {
      target.open = true;
    }
    target?.scrollIntoView();
    // The focus follows, or the keyboard and the screen reader stay on the header's "Hilfe"
    // while the page shows the entry; an entry is reached through its summary.
    const focusable = target instanceof HTMLDetailsElement ? target.querySelector("summary") : target;
    if (focusable instanceof HTMLElement) {
      if (focusable.tagName !== "SUMMARY") {
        focusable.tabIndex = -1;
      }
      focusable.focus({ preventScroll: true });
    }
  }, [hash]);

  return (
    <>
      <PageHeading title="Hilfe" />

      {/* Moved here from the header (19.10.2026): who may do what is a question for the help. */}
      <p className="-mt-3 mb-6 text-sm text-muted-foreground">
        Ihre Berechtigungen:{" "}
        {roles.map((role) => PERMISSION_LABELS[role as Permission] ?? role).join(", ")}
      </p>

      <nav aria-label="Abschnitte" className="mb-8">
        <ul className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
          {HELP_SECTIONS.map((section) => (
            <li key={section.id}>
              <a
                className="underline underline-offset-4 hover:text-muted-foreground"
                href={`#${section.id}`}
              >
                {section.title}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      {HELP_SECTIONS.map((section) => (
        <section key={section.id} id={section.id} className="mb-8 scroll-mt-4">
          <h2 className="mb-3 text-lg font-bold">
            {section.title}
          </h2>
          <div className="border border-border">
            {section.entries.map((entry) => (
              <Entry key={entry.id} entry={entry} />
            ))}
          </div>
        </section>
      ))}

      <p className="text-xs text-muted-foreground">Stand: {HELP_UPDATED_AT}</p>
    </>
  );
}
